import React, { useState, useEffect } from 'react';
import { amDesignColors, amDesignTypography, amDesignComponents } from '../../themes/amDesignSystem';
import {
  Paper,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Pagination,
  Button,
} from '@mui/material';
import {
  Folder as FolderIcon,
  InsertDriveFile as FileIcon,
  Image as ImageIcon,
  VideoLibrary as VideoIcon,
  Description as DocumentIcon,
  Download as DownloadIcon,
  Close as CloseIcon,
  CloudDownload as CloudDownloadIcon,
  CloudUpload as CloudUploadIcon,
  LinearProgress as LinearProgressIcon,
} from '@mui/icons-material';
import LinearProgress from '@mui/material/LinearProgress';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useBusiness } from '../../contexts/BusinessContext';
import axios from 'axios';

const LibraryPage = () => {
  const { locationId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedBusiness } = useBusiness();
  const [businessProfile, setBusinessProfile] = useState(null);
  const [library, setLibrary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [currentFolder, setCurrentFolder] = useState(null); // Track current folder ID
  const [folderStack, setFolderStack] = useState([]); // Track folder path with names
  const [uploading, setUploading] = useState(false); // Track upload state
  const [uploadProgress, setUploadProgress] = useState(0); // Track upload progress
  const fileInputRef = React.useRef(null); // Reference to file input
  const itemsPerPage = 12;

  // Only navigate if already on a /library route, not when embedded in /posts
  // This prevents navigating away from /posts when switching businesses
  useEffect(() => {
    if (selectedBusiness && location.pathname.includes('/library/')) {
      const selectedLocationId = selectedBusiness.locationId || selectedBusiness.googleLocationId || selectedBusiness.id;
      if (selectedLocationId && selectedLocationId !== locationId) {
        navigate(`/library/${selectedLocationId}`);
      }
    }
  }, [selectedBusiness, locationId, navigate, location.pathname]);

  // When locationId changes, reset folder state and fetch new library
  useEffect(() => {
    setCurrentFolder(null);
    setFolderStack([]);
    setPage(1);
    setLibrary(null);
    setSelectedItem(null);
    setPreviewOpen(false);
  }, [locationId]);

  // Fetch library when locationId or currentFolder changes
  useEffect(() => {
    fetchLibrary();
  }, [locationId, currentFolder]);

  const fetchLibrary = async () => {
    try {
      setLoading(true);
      setError(null);

      // Use cached profile if available
      let profile = businessProfile;
      if (!profile) {
        const profileResponse = await axios.get(
          `/api/businesses/${locationId}/details`,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
            },
            timeout: 8000
          }
        );
        profile = profileResponse.data;
      }

      if (!profile?.contactCloudTenantId) {
        setError('Business profile does not have Contact Cloud Tenant ID configured');
        setLibrary([]);
        setLoading(false);
        return;
      }

      setBusinessProfile(profile);

      // Fetch library with timeout
      let libraryUrl = `/api/businesses/${locationId}/library?sortMode=descending&limit=60&page=1`;
      if (currentFolder) {
        libraryUrl += `&parent=${currentFolder}`;
      }

      const libraryResponse = await axios.get(libraryUrl, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        timeout: 10000
      });

      const documents = libraryResponse.data.rows?.rows?.documents || [];
      setLibrary(documents);
      setError(null);
    } catch (err) {
      let errorMsg = 'Failed to fetch library';
      if (err.response?.status === 401) {
        errorMsg = 'Authentication failed';
      } else if (err.response?.status === 403) {
        errorMsg = 'Access denied';
      } else if (err.code === 'ECONNABORTED') {
        errorMsg = 'Request timeout';
      }
      setError(errorMsg);
      setLibrary([]);
    } finally {
      setLoading(false);
    }
  };

  const getFileIcon = (item) => {
    if (item.isFolder) {
      return <FolderIcon sx={{ fontSize: 40, color: amDesignColors.warning }} />;
    }

    const fileType = item.details?.fileType || '';
    const extension = item.details?.extension || '';

    if (fileType.startsWith('image/')) {
      return <ImageIcon sx={{ fontSize: 40, color: amDesignColors.primary }} />;
    } else if (fileType.startsWith('video/')) {
      return <VideoIcon sx={{ fontSize: 40, color: amDesignColors.error }} />;
    } else if (fileType.includes('pdf') || fileType.includes('document')) {
      return <DocumentIcon sx={{ fontSize: 40, color: '#9C27B0' }} />;
    }
    return <FileIcon sx={{ fontSize: 40, color: amDesignColors.text.tertiary }} />;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    const b = parseInt(bytes);
    if (b === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(b) / Math.log(k));
    return Math.round((b / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const openFolder = (folder) => {
    // Push current folder to stack
    setFolderStack([...folderStack, { id: currentFolder, name: currentFolder === null ? 'Library' : 'Back' }]);
    setCurrentFolder(folder._id);
    setPage(1);
  };

  const goBack = () => {
    if (folderStack.length > 0) {
      const newStack = [...folderStack];
      const previousFolder = newStack.pop();
      setFolderStack(newStack);
      setCurrentFolder(previousFolder.id);
      setPage(1);
    }
  };

  const goToRoot = () => {
    setCurrentFolder(null);
    setFolderStack([]);
    setPage(1);
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setUploading(true);
      setUploadProgress(0);

      // Step 1: Request upload URL
      setUploadProgress(25);

      const uploadUrlResponse = await axios.post(
        `/api/businesses/${locationId}/library`,
        {
          fileName: file.name,
          fileType: file.type,
          fileSize: file.size,
          parent: currentFolder
        },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          },
          timeout: 10000 // 10s timeout
        }
      );

      const { id: fileId, url: s3Url, fields } = uploadUrlResponse.data;
      setUploadProgress(50);

      // Step 2: Upload to S3 (use Fetch API to avoid axios Authorization header)
      setUploadProgress(75);

      const formData = new FormData();
      Object.entries(fields).forEach(([key, value]) => {
        formData.append(key, value);
      });
      formData.append('file', file);

      const uploadResponse = await fetch(s3Url, {
        method: 'POST',
        body: formData
      });

      if (!uploadResponse.ok) {
        const errorText = await uploadResponse.text();
        throw new Error(`S3 upload failed: ${uploadResponse.status}`);
      }

      setUploadProgress(90);

      // Step 3: Confirm upload
      await axios.put(
        `/api/businesses/${locationId}/library/${fileId}`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          },
          timeout: 10000 // 10s timeout
        }
      );

      setUploadProgress(100);

      // Refresh library and close dialog immediately
      setUploading(false);
      setUploadProgress(0);
      fetchLibrary();
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      console.error('❌ Upload error:', error);
      setError(`Upload failed: ${error.response?.data?.message || error.message}`);
      setUploading(false);
      setUploadProgress(0);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Separate folders and files, with folders first
  const folders = (library || []).filter(item => item.isFolder);
  const files = (library || []).filter(item => !item.isFolder);
  const allItems = [...folders, ...files];

  // Pagination
  const totalPages = Math.ceil(allItems.length / itemsPerPage);
  const startIdx = (page - 1) * itemsPerPage;
  const paginatedItems = allItems.slice(startIdx, startIdx + itemsPerPage);

  return (
    <Box sx={{ py: 2 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
              Library
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {businessProfile?.businessName || 'Business'}
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<CloudUploadIcon />}
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
          >
            Upload File
          </Button>
        </Box>

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          hidden
          onChange={handleFileSelect}
          accept="*"
        />

        {/* Navigation Buttons */}
        {currentFolder !== null && (
          <Box sx={{ mb: 2, display: 'flex', gap: 2 }}>
            <Button
              size="small"
              variant="outlined"
              onClick={goToRoot}
            >
               Back to Library
            </Button>
            {folderStack.length > 0 && (
              <Button
                size="small"
                variant="outlined"
                onClick={goBack}
              >
                ← Back
              </Button>
            )}
          </Box>
        )}
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Stats */}
      {!error && library && (
        <Box sx={{ mb: 4, display: 'flex', gap: 2 }}>
          <Chip
            icon={<FolderIcon />}
            label={`${folders.length} Folders`}
            variant="outlined"
            color="primary"
          />
          <Chip
            icon={<FileIcon />}
            label={`${files.length} Files`}
            variant="outlined"
            color="secondary"
          />
        </Box>
      )}

      {/* Library Grid */}
      {library && library.length > 0 ? (
        <>
          <Grid container spacing={2} sx={{ mb: 4 }}>
            {paginatedItems.map((item) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={item._id}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    cursor: 'pointer',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 16px rgba(0,0,0,0.15)',
                    },
                  }}
                  onClick={() => {
                    if (item.isFolder) {
                      openFolder(item);
                    } else {
                      setSelectedItem(item);
                      setPreviewOpen(true);
                    }
                  }}
                >
                  {/* Thumbnail */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minHeight: 200,
                      bgcolor: '#f5f5f5',
                      position: 'relative',
                    }}
                  >
                    {item.isFolder ? (
                      <FolderIcon sx={{ fontSize: 60, color: amDesignColors.warning }} />
                    ) : item.details?.fileType?.startsWith('image/') ? (
                      <Box
                        component="img"
                        src={`${item.url}/${item.key}`}
                        alt={item.name}
                        sx={{
                          width: '100%',
                          height: 200,
                          objectFit: 'cover',
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.parentElement.innerHTML = '<div style="display: flex; align-items: center; justify-content: center; height: 100%; width: 100%;"><svg style="font-size: 40px; color: amDesignColors.primary;">📷</svg></div>';
                        }}
                      />
                    ) : (
                      getFileIcon(item)
                    )}
                    {!item.isFolder && (
                      <Chip
                        label={item.details?.extension || 'FILE'}
                        size="small"
                        sx={{
                          position: 'absolute',
                          bottom: 8,
                          right: 8,
                          bgcolor: 'rgba(0,0,0,0.7)',
                          color: amDesignColors.white,
                        }}
                      />
                    )}
                  </Box>

                  {/* Content */}
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 'bold',
                        mb: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {item.name}
                    </Typography>

                    {item.isFolder ? (
                      <Chip
                        size="small"
                        label={`${item.totalFileCount} items`}
                        variant="outlined"
                      />
                    ) : (
                      <>
                        <Typography variant="caption" display="block" color="textSecondary">
                          {formatFileSize(item.details?.fileSize)}
                        </Typography>
                        <Typography variant="caption" display="block" color="textSecondary">
                          {formatDate(item.createdAt)}
                        </Typography>
                      </>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(e, value) => setPage(value)}
                color="primary"
              />
            </Box>
          )}
        </>
      ) : (
        !error && (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <CloudDownloadIcon sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
            <Typography variant="h6" color="textSecondary">
              No files or folders in library
            </Typography>
          </Paper>
        )
      )}

      {/* Upload Progress Dialog */}
      <Dialog
        open={uploading}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown
        onClose={(e) => e.preventDefault()}
      >
        <DialogTitle>Uploading File</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <LinearProgress variant="determinate" value={uploadProgress} />
            <Typography variant="body2" color="textSecondary" sx={{ mt: 2, textAlign: 'center' }}>
              {uploadProgress}%
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="md"
        fullWidth
      >
        {selectedItem && (
          <>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">{selectedItem.name}</Typography>
              <IconButton onClick={() => setPreviewOpen(false)}>
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent>
              {selectedItem.isFolder ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <FolderIcon sx={{ fontSize: 80, color: amDesignColors.warning, mb: 2 }} />
                  <Typography variant="body1" gutterBottom>
                    Folder: {selectedItem.name}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Contains {selectedItem.totalFileCount} items
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ textAlign: 'center' }}>
                  {selectedItem.details?.fileType?.startsWith('image/') ? (
                    <Box
                      component="img"
                      src={`${selectedItem.url}/${selectedItem.key}`}
                      alt={selectedItem.name}
                      sx={{ maxWidth: '100%', maxHeight: 500 }}
                    />
                  ) : selectedItem.details?.fileType?.startsWith('video/') ? (
                    <video
                      controls
                      style={{ maxWidth: '100%', maxHeight: 500 }}
                      src={`${selectedItem.url}/${selectedItem.key}`}
                    />
                  ) : (
                    <Box sx={{ py: 4 }}>
                      {getFileIcon(selectedItem)}
                      <Typography variant="body1" sx={{ mt: 2 }}>
                        {selectedItem.name}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {selectedItem.details?.fileType}
                      </Typography>
                    </Box>
                  )}

                  <Box sx={{ mt: 3, textAlign: 'left' }}>
                    <Typography variant="body2">
                      <strong>Size:</strong> {formatFileSize(selectedItem.details?.fileSize)}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Type:</strong> {selectedItem.details?.fileType}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Created:</strong> {formatDate(selectedItem.createdAt)}
                    </Typography>
                  </Box>
                </Box>
              )}
            </DialogContent>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default LibraryPage;
